import { type DevelopmentDataConfig } from "@repo/api";
import { EnvironmentHelper } from "@repo/helpers";
import { LocalStorageKeys } from "@repo/logic";
import { isEqual } from "lodash";
import { createContext, type FC, type PropsWithChildren, useContext, useState } from "react";

type DataConfig = DevelopmentDataConfig & { overrideToken: string | null };
type IDataConfig = {
    config: DataConfig;
    setConfig: (config: DataConfig) => void;
    resetConfig: () => void;
};

export const defaultConfig: DataConfig = {
    includeFufi: true,
    overrideToken: null,
};

const getIsDefaultConfig = (newConfig: DataConfig) => {
    return isEqual(newConfig, defaultConfig);
};

const loadConfig = (): DataConfig => {
    const configString = EnvironmentHelper.isBrowser() ? localStorage.getItem(LocalStorageKeys.DATA_CONFIG) : null;
    if (configString === null) return defaultConfig;
    return JSON.parse(configString) as DataConfig;
};

const DataConfigContext = createContext<IDataConfig>({} as IDataConfig);

export const useDataConfig = (): IDataConfig => {
    return useContext(DataConfigContext);
};

export const DataConfigContextProvider: FC<PropsWithChildren> = ({ children }) => {
    const [config, setConfig] = useState<DataConfig>(loadConfig());

    return (
        <DataConfigContext.Provider
            value={{
                config,
                setConfig: (config: DataConfig) => {
                    !getIsDefaultConfig(config)
                        ? localStorage.setItem(LocalStorageKeys.DATA_CONFIG, JSON.stringify(config))
                        : localStorage.removeItem(LocalStorageKeys.DATA_CONFIG);
                    setConfig(config);
                },
                resetConfig: () => {
                    setConfig(defaultConfig);
                    localStorage.removeItem(LocalStorageKeys.DATA_CONFIG);
                },
            }}
        >
            {children}
        </DataConfigContext.Provider>
    );
};
